interface Props {
  readonly holes: number;
  readonly picked: number | null;
  readonly answer: number;
  readonly revealed: boolean;
  readonly onPick: (index: number) => void;
}

const RADIUS = 100;

/** Holes laid out counterclockwise from 3 o'clock, matching machine convention. */
export function BoltCircle({ holes, picked, answer, revealed, onPick }: Props) {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '6px 0' }}>
      <div style={{ position: 'relative', width: 250, height: 250 }}>
        <div
          style={{
            position: 'absolute',
            inset: 25,
            borderRadius: 999,
            border: '2px dashed rgba(255,255,255,.22)',
          }}
        />
        <div
          style={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            width: 9,
            height: 9,
            margin: '-4.5px 0 0 -4.5px',
            borderRadius: 999,
            background: 'rgba(255,255,255,.4)',
          }}
        />
        {/* Reference line out to 0°, so "counterclockwise from here" is visible. */}
        <div
          style={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            width: RADIUS,
            height: 1,
            background: 'rgba(255,255,255,.18)',
          }}
        />
        <div
          style={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            font: '500 10px/1 var(--font-body)',
            color: 'var(--text-4)',
            transform: 'translate(36px,-17px)',
          }}
        >
          0°
        </div>

        {Array.from({ length: holes }, (_, i) => {
          const angle = (i * 360) / holes;
          const rad = (angle * Math.PI) / 180;
          const isPicked = picked === i;
          const isAnswer = revealed && i === answer;
          const isWrong = revealed && isPicked && i !== answer;

          const colour = isAnswer
            ? 'var(--lime)'
            : isWrong
              ? 'var(--red)'
              : isPicked
                ? 'var(--lime)'
                : 'rgba(255,255,255,.35)';

          return (
            <button
              key={i}
              type="button"
              onClick={() => onPick(i)}
              disabled={revealed}
              aria-label={`Hole ${i + 1} at ${Math.round(angle)} degrees`}
              aria-pressed={isPicked}
              style={{
                position: 'absolute',
                left: '50%',
                top: '50%',
                width: 40,
                height: 40,
                marginLeft: -20,
                marginTop: -20,
                // Y is negated: screen coordinates run down, angles run up.
                transform: `translate(${(Math.cos(rad) * RADIUS).toFixed(1)}px, ${(-Math.sin(rad) * RADIUS).toFixed(1)}px)`,
                borderRadius: 999,
                border: `2px solid ${colour}`,
                background: isPicked || isAnswer ? 'oklch(0.82 0.17 130 / .16)' : 'var(--bg)',
                color: colour,
                font: '700 13px/1 var(--font-display)',
                cursor: revealed ? 'default' : 'pointer',
                transition: 'border-color var(--motion), background var(--motion)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {isAnswer ? '✓' : isWrong ? '✕' : ''}
            </button>
          );
        })}
      </div>
    </div>
  );
}
